import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import type { CoverageWarning } from '@conciair/scheduler-core';
import { useDrawer } from '../../app/Drawer';
import { Chip, ErrorNote, Spinner } from '../../ui/components';
import { fmtDate } from '../../ui/format';
import { AREA_TO_PATH, useProblems, type Problem } from './useLiveChecks';

/**
 * Input problems (V2 D80): the validation messages and coverage forecast
 * that used to be Home's Attention card, now a top-bar chip that opens a
 * drawer. This is what gates Generate, so it lives beside Generate.
 */
export function InputProblemsChip({ orgId }: { orgId: string }) {
  const { checks, problems } = useProblems(orgId);
  const drawer = useDrawer();
  // Shown only when an input error blocks Generate (D89). Warnings live on
  // the pages that own them; the coverage forecast is in Findings.
  if (checks.isLoading || problems.errors === 0) return null;
  const open = () => drawer.open({ title: 'Input problems', subtitle: 'Fix these to generate a draft', body: <InputProblemsList orgId={orgId} /> });
  return (
    <button type="button" onClick={open} title="What is blocking Generate" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold">
      <Chip tone="danger"><TriangleAlert size={12} /> {problems.errors} input error{problems.errors === 1 ? '' : 's'}</Chip>
    </button>
  );
}

export function InputProblemsList({ orgId }: { orgId: string }) {
  const { checks, problems } = useProblems(orgId);
  const drawer = useDrawer();
  if (checks.isLoading) return <Spinner label="Validating inputs" />;
  if (checks.error) return <ErrorNote message={checks.error.message} />;
  const coverage = checks.data?.coverage?.coverageWarnings ?? [];
  return (
    <div className="space-y-5 text-sm">
      <section>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-navy">Validation</h3>
        <ProblemLinks problems={problems.all} onFollow={drawer.close} />
      </section>
      <section>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-navy">Coverage forecast</h3>
        {!checks.data?.coverage && problems.errors > 0 && <p className="text-ink-muted">Runs once the errors above are fixed.</p>}
        {checks.data?.coverage && <CoverageList warnings={coverage} />}
      </section>
    </div>
  );
}

function ProblemLinks({ problems, onFollow }: { problems: Problem[]; onFollow(): void }) {
  if (problems.length === 0) return <p className="text-ok">Everything validates.</p>;
  return (
    <ul className="space-y-1.5">
      {problems.map((p, i) => {
        const path = AREA_TO_PATH[p.area] ?? '/scheduler/schedule';
        const href = p.ref ? `${path}${path.includes('?') ? '&' : '?'}ref=${p.ref}` : path;
        return (
          <li key={i} className="flex items-start gap-2">
            <Chip tone={p.level === 'error' ? 'danger' : 'warn'} className="mt-0.5 shrink-0">{p.level === 'error' ? 'Error' : 'Warning'}</Chip>
            <span>
              <Link to={href} onClick={onFollow} className="font-medium text-navy underline">
                {p.area}{p.row ? ` record ${p.row}` : ''}
              </Link>
              : {p.message}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function CoverageList({ warnings }: { warnings: CoverageWarning[] }) {
  if (warnings.length === 0) return <p className="text-ok">Every type and seat pool covers the whole window.</p>;
  return (
    <ul className="space-y-1.5">
      {warnings.map((w, i) => (
        <li key={i} className="flex items-start gap-2">
          <Chip tone="warn" className="mt-0.5 shrink-0">{w.kind}</Chip>
          <span>
            <span className="font-medium">{w.type} {w.seat}</span> from {fmtDate(w.startIso)}: {w.message}
          </span>
        </li>
      ))}
    </ul>
  );
}
